/**
 * useAndroidBackButton (T27.2.7.3) — the single app-level handler for the Android
 * hardware Back button. Mounted ONCE in AppShell.
 *
 * The problem (assessment X1): there is no `App.addListener('backButton', …)`
 * anywhere, and every overlay holds its open-state in React with no link to
 * history. So Back falls through to Capacitor's default (`history.back()`), skips
 * the open overlay, and pops the route under it — or exits the app. That is the
 * single root of "expanded deal → back gets me lost" (DL1), "deal link won't
 * bring me back" (DL2), and "menu back redirects me" (MN2).
 *
 * The decision tree on each Back press (see `decideBackAction`):
 *   1. An overlay is open  → close the topmost one, stop there (the press is
 *      consumed by the overlay, the page underneath is untouched).
 *   2. Else, history can go back → `history.back()` (router back).
 *   3. Else (at the history root) → let the app minimize (Capacitor `exitApp()`).
 *
 * Native-only: when Capacitor registers a `backButton` listener it suppresses the
 * default minimize and hands US full control — so on a native platform we must
 * cover every branch (including the root minimize). On web there is no hardware
 * Back, so the hook never registers a listener and the existing Escape / backdrop
 * close paths run unchanged (AC5).
 *
 * The plugin is dynamically imported only after `isNativePlatform()` so the eager
 * web bundle never loads the native-only `@capacitor/app` module — same guard
 * pattern as useNativeAppVersion.ts / nativeGoogleSignIn.ts. Every Capacitor
 * import lives here in `apps/web`; nothing Capacitor-shaped reaches
 * `packages/shared` (CP1–CP4).
 */

import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { closeTopmost, hasOpenOverlay } from './overlayRegistry';

/** What the hardware-Back press should do this time. */
export type BackAction =
  /** An overlay was open and has been closed; consume the press. */
  | 'close-overlay'
  /** Nothing open and history can go back; navigate back. */
  | 'history-back'
  /** Nothing open and at the history root; let the app minimize. */
  | 'minimize';

/**
 * Pure decision core — given the current overlay + history state, decide and
 * enact the close (the only side effect is `closeTopmost`, which the registry
 * owns). Returns which branch ran so the caller performs the navigation/exit and
 * tests can assert the tree without Capacitor.
 *
 * `canGoBack` comes from Capacitor's backButton event payload; it mirrors whether
 * there is web history to pop.
 */
export function decideBackAction(canGoBack: boolean): BackAction {
  if (hasOpenOverlay()) {
    closeTopmost();
    return 'close-overlay';
  }
  if (canGoBack) return 'history-back';
  return 'minimize';
}

export function useAndroidBackButton(): void {
  useEffect(() => {
    // No hardware Back on web — never register a listener (AC5). The existing
    // Escape / backdrop close paths handle overlay dismissal on web/desktop.
    if (!Capacitor.isNativePlatform()) return undefined;

    let removeListener: (() => void) | null = null;
    let cancelled = false;

    // Dynamic import keeps the native-only plugin out of the eager web bundle.
    void import('@capacitor/app').then(({ App }) => {
      if (cancelled) {
        // The component unmounted before the import resolved — nothing to bind.
        return;
      }
      void App.addListener('backButton', ({ canGoBack }) => {
        const action = decideBackAction(canGoBack);
        if (action === 'close-overlay') {
          // Overlay closed by decideBackAction; consume the press (do nothing
          // else so the underlying route stays put).
          return;
        }
        if (action === 'history-back') {
          window.history.back();
          return;
        }
        // At the history root with nothing open — minimize the app (Capacitor's
        // default, which our listener otherwise suppresses).
        void App.exitApp();
      }).then((handle) => {
        if (cancelled) {
          // Unmounted while addListener was resolving — remove immediately.
          void handle.remove();
          return;
        }
        removeListener = () => void handle.remove();
      });
    });

    return () => {
      cancelled = true;
      removeListener?.();
    };
  }, []);
}
